import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, Search, ShoppingBag, Heart, User, Menu, X, Instagram, LogOut, Mail } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../lib/auth";
import { useShop } from "../lib/shop";
import { NotificationBell } from "./NotificationBell";
import { api } from "../lib/api";
import { Toaster } from "sonner";

const navLinks = [
  { to: "/products", label: "Shop All" },
  { to: "/products?category=necklaces", label: "Jewellery" },
  { to: "/products?category=kids-gifts", label: "Kids' Gifts" },
  { to: "/about", label: "About" },
];

export const Layout = () => {
  const { user, logout } = useAuth();
  const { cart, wishlist } = useShop();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [pub, setPub] = useState({ announcement_text: "Free shipping across India on orders ₹499+ • Cash on Delivery available", support_email: "joypearlvske@gmail.com" });
  const navigate = useNavigate();
  const cartCount = cart?.items?.length || 0;
  const wishCount = wishlist?.product_ids?.length || 0;

  useEffect(() => { api.get("/settings/public").then((r) => setPub(r.data)).catch(() => {}); }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (searchValue.trim()) navigate(`/products?q=${encodeURIComponent(searchValue.trim())}`);
    setSearchOpen(false);
  };

  return (
    <div className="min-h-screen bg-brand-bg text-brand-ink flex flex-col">
      {/* Announcement strip */}
      <div className="bg-brand-primary text-white text-[11px] tracking-[0.2em] uppercase py-2 text-center" data-testid="announcement-strip">
        {pub.announcement_text}
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 w-full backdrop-blur-md bg-brand-bg/80 border-b border-brand-line">
        <div className="jp-container flex items-center justify-between h-16">
          <button className="md:hidden p-2 -ml-2" onClick={() => setMenuOpen(true)} aria-label="Menu" data-testid="mobile-menu-button">
            <Menu size={20} />
          </button>
          <Logo />
          <nav className="hidden md:flex items-center gap-7">
            {navLinks.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s|'/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors ${isActive ? "text-brand-primary" : "text-brand-ink hover:text-brand-primary"}`
                }>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-1 sm:gap-2">
            <button onClick={() => setSearchOpen((s) => !s)} className="p-2" aria-label="Search" data-testid="header-search-button">
              <Search size={18} />
            </button>
            <Link to="/wishlist" className="p-2 relative hidden sm:inline-flex" data-testid="header-wishlist-link">
              <Heart size={18} />
              {wishCount > 0 && <span className="absolute -top-0.5 -right-0.5 bg-brand-primary text-white text-[10px] rounded-full h-4 min-w-4 px-1 inline-flex items-center justify-center">{wishCount}</span>}
            </Link>
            <Link to="/cart" className="p-2 relative" data-testid="header-cart-link">
              <ShoppingBag size={18} />
              {cartCount > 0 && <span className="absolute -top-0.5 -right-0.5 bg-brand-primary text-white text-[10px] rounded-full h-4 min-w-4 px-1 inline-flex items-center justify-center">{cartCount}</span>}
            </Link>
            <NotificationBell />
            <Link to={user ? "/account" : "/login"} className="p-2 hidden sm:inline-flex" data-testid="header-account-link">
              <User size={18} />
            </Link>
          </div>
        </div>
        {searchOpen && (
          <div className="border-t border-brand-line bg-white">
            <form onSubmit={onSearch} className="jp-container flex items-center gap-3 py-3" data-testid="search-bar-form">
              <Search size={18} className="text-brand-muted" />
              <input
                autoFocus
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Search for jhumkas, pearl necklaces, soft toys…"
                className="jp-input flex-1 border-none focus:ring-0"
                data-testid="search-input"
              />
              <button type="submit" className="jp-btn-primary px-4 py-2 text-xs" data-testid="search-submit">Search</button>
            </form>
          </div>
        )}
      </header>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-menu-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMenuOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-[78%] max-w-sm bg-white p-6 flex flex-col gap-1">
            <div className="flex items-center justify-between mb-6">
              <Logo />
              <button onClick={() => setMenuOpen(false)} aria-label="Close" data-testid="mobile-menu-close"><X size={20} /></button>
            </div>
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setMenuOpen(false)} className="py-3 text-lg font-serif border-b border-brand-line">{l.label}</Link>
            ))}
            <Link to="/orders" onClick={() => setMenuOpen(false)} className="py-3 text-lg font-serif border-b border-brand-line">My Orders</Link>
            {user?.role === "seller" && <Link to="/seller" onClick={() => setMenuOpen(false)} className="py-3 text-lg font-serif border-b border-brand-line">Seller Dashboard</Link>}
            {user?.role === "admin" && <Link to="/admin" onClick={() => setMenuOpen(false)} className="py-3 text-lg font-serif border-b border-brand-line">Admin</Link>}
            <div className="mt-auto pt-6">
              {user ? (
                <button onClick={() => { logout(); setMenuOpen(false); navigate("/"); }} className="jp-btn-outline w-full" data-testid="mobile-logout"><LogOut size={14} className="mr-2"/>Sign Out</button>
              ) : (
                <Link to="/login" onClick={() => setMenuOpen(false)} className="jp-btn-primary w-full" data-testid="mobile-login">Sign In</Link>
              )}
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 pb-20 md:pb-0">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-brand-line mt-12">
        <div className="jp-container py-12 grid gap-8 md:grid-cols-4">
          <div>
            <Logo />
            <p className="mt-4 text-sm text-brand-muted leading-relaxed">
              Premium artificial jewellery and curated kids' gifts. Crafted with love, delivered with care across India.
            </p>
            <a href="https://www.instagram.com/joypearl_official" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 text-sm text-brand-primary hover:underline" data-testid="footer-instagram">
              <Instagram size={16} /> @joypearl_official
            </a>
            <a href={`mailto:${pub.support_email}`} className="inline-flex items-center gap-2 mt-2 text-sm text-brand-muted hover:text-brand-primary" data-testid="footer-email">
              <Mail size={16}/> {pub.support_email}
            </a>
            <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-brand-muted">🇮🇳 Made & shipped in India only</p>
          </div>
          <div>
            <h4 className="font-serif text-lg mb-4">Shop</h4>
            <ul className="space-y-2 text-sm text-brand-muted">
              <li><Link to="/products?category=necklaces" className="hover:text-brand-primary">Necklaces</Link></li>
              <li><Link to="/products?category=earrings" className="hover:text-brand-primary">Earrings</Link></li>
              <li><Link to="/products?category=bangles" className="hover:text-brand-primary">Bangles</Link></li>
              <li><Link to="/products?category=kids-gifts" className="hover:text-brand-primary">Kids' Gifts</Link></li>
              <li><Link to="/products?category=soft-toys" className="hover:text-brand-primary">Soft Toys</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-serif text-lg mb-4">Company</h4>
            <ul className="space-y-2 text-sm text-brand-muted">
              <li><Link to="/about" className="hover:text-brand-primary">About Joy Pearl</Link></li>
              <li><Link to="/contact" className="hover:text-brand-primary">Contact</Link></li>
              <li><Link to="/legal/terms" className="hover:text-brand-primary">Terms & Conditions</Link></li>
              <li><Link to="/legal/privacy" className="hover:text-brand-primary">Privacy Policy</Link></li>
              <li><Link to="/legal/dpdp" className="hover:text-brand-primary">DPDP / Grievance</Link></li>
              <li><Link to="/legal/shipping" className="hover:text-brand-primary">Shipping Policy</Link></li>
              <li><Link to="/legal/returns" className="hover:text-brand-primary">Returns & Refunds</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-serif text-lg mb-4">Legal Entity</h4>
            <address className="not-italic text-sm text-brand-muted leading-relaxed">
              <strong className="text-brand-ink font-medium">Vasudevaya Sreekrishna Enterprises</strong><br />
              Sector 84, Gurgaon<br />
              Haryana 122004, India<br />
              <span className="text-xs">GSTIN: 06BITPK7883H1ZH</span>
            </address>
          </div>
        </div>
        <div className="border-t border-brand-line">
          <div className="jp-container py-4 text-xs text-brand-muted flex flex-col sm:flex-row items-center justify-between gap-2">
            <span>© {new Date().getFullYear()} Vasudevaya Sreekrishna Enterprises. All rights reserved.</span>
            <span>Joy Pearl • vskstore.com • India only</span>
          </div>
        </div>
      </footer>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 h-16 bg-white border-t border-brand-line flex justify-around items-center md:hidden" data-testid="mobile-bottom-nav">
        <NavLink to="/" end className={({isActive}) => `flex flex-col items-center gap-0.5 text-[10px] ${isActive ? "text-brand-primary" : "text-brand-muted"}`} data-testid="bn-home"><Home size={20}/>Home</NavLink>
        <NavLink to="/products" className={({isActive}) => `flex flex-col items-center gap-0.5 text-[10px] ${isActive ? "text-brand-primary" : "text-brand-muted"}`} data-testid="bn-shop"><Search size={20}/>Shop</NavLink>
        <NavLink to="/cart" className={({isActive}) => `flex flex-col items-center gap-0.5 text-[10px] relative ${isActive ? "text-brand-primary" : "text-brand-muted"}`} data-testid="bn-cart">
          <ShoppingBag size={20}/>
          {cartCount > 0 && <span className="absolute top-0 right-3 bg-brand-primary text-white text-[9px] rounded-full h-4 min-w-4 px-1 inline-flex items-center justify-center">{cartCount}</span>}
          Cart
        </NavLink>
        <NavLink to="/wishlist" className={({isActive}) => `flex flex-col items-center gap-0.5 text-[10px] ${isActive ? "text-brand-primary" : "text-brand-muted"}`} data-testid="bn-wishlist"><Heart size={20}/>Wishlist</NavLink>
        <NavLink to={user ? "/account" : "/login"} className={({isActive}) => `flex flex-col items-center gap-0.5 text-[10px] ${isActive ? "text-brand-primary" : "text-brand-muted"}`} data-testid="bn-account"><User size={20}/>Account</NavLink>
      </nav>

      <Toaster position="top-center" richColors closeButton />
    </div>
  );
};
